/**
 * Simli: audio in, lip-synced face out, over one WebRTC connection that
 * carries the face's video and the voice it is speaking, so the two stay in
 * step. The library is loaded only when this trial is switched on.
 */
import { makeResampler } from './resample';

const CHUNK = 6000; // bytes of 16 kHz PCM16 per send, as Simli's own examples use
const RATE = 16000;

let library = null;
/** Fetch the library once; the start screen calls this so Begin needn't wait for it. */
export function loadSimli() {
  // simli-client 3.0.2's index requires './Client' but ships 'client.js',
  // which resolves on a case-insensitive disk (macOS) and fails on Linux,
  // where the production build runs. Import the file itself.
  if (!library) {
    library = import('simli-client/dist/client');
    library.catch(() => { library = null; });
  }
  return library;
}

export async function createSimliRenderer(session, { video, audio }) {
  const { SimliClient, LogLevel } = await loadSimli();
  const client = new SimliClient(session.session_token, video, audio, null, LogLevel.ERROR, 'livekit');
  let waiting = null;

  const finish = () => {
    if (!waiting) return;
    clearTimeout(waiting.timer);
    const { resolve } = waiting;
    waiting = null;
    resolve();
  };
  // "silent" also arrives while nothing has been said yet, so a line is only
  // over once the face has started speaking it and gone quiet again - and,
  // for a line still streaming in, once all of it has been sent.
  client.on('speaking', () => { if (waiting) waiting.spoke = true; });
  client.on('silent', () => { if (waiting?.spoke && waiting.sent) finish(); });
  client.on('stop', finish);
  client.on('error', finish);
  await client.start();

  return {
    provider: 'simli',
    sampleRate: 16000,
    say(pcm, seconds) {
      finish();
      return new Promise((resolve) => {
        waiting = { resolve, spoke: false, sent: true, timer: setTimeout(finish, (seconds + 4) * 1000) };
        for (let i = 0; i < pcm.length; i += CHUNK) client.sendAudioData(pcm.subarray(i, i + CHUNK));
      });
    },
    /** A line as it streams in: push() each Int16Array piece at sourceRate,
     * then end(), which resolves once the face has finished saying it. */
    open(sourceRate) {
      finish();
      const resample = makeResampler(sourceRate, RATE);
      let samples = 0;
      let startedAt = null;
      const line = new Promise((resolve) => { waiting = { resolve, spoke: false, sent: false, timer: null }; });
      return {
        push(piece) {
          const out = resample(piece);
          if (!out.length) return;
          if (startedAt === null) startedAt = Date.now();
          samples += out.length;
          const bytes = new Uint8Array(out.buffer);
          for (let i = 0; i < bytes.length; i += CHUNK) client.sendAudioData(bytes.subarray(i, i + CHUNK));
        },
        end() {
          if (waiting) {
            waiting.sent = true;
            // What is left to play, plus room for Simli's own delay.
            const elapsed = startedAt === null ? 0 : (Date.now() - startedAt) / 1000;
            waiting.timer = setTimeout(finish, (Math.max(0, samples / RATE - elapsed) + 3) * 1000);
          }
          return line;
        },
      };
    },
    interrupt() {
      client.ClearBuffer();
      finish();
    },
    async stop() {
      finish();
      try { await client.stop(); } catch { /* already closed */ }
    },
  };
}
